import React, { useState, useEffect } from 'react';
import categoriaService from '../services/categoriaService';
import { toast } from 'react-toastify';

const CategoriasServicios = () => {
  // Estado para categorías, subcategorías y servicios
  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [servicios, setServicios] = useState([]);

  // Estado para elementos seleccionados
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState(null);
  const [subcategoriaSeleccionada, setSubcategoriaSeleccionada] = useState(null);

  // Estado para nuevos elementos
  const [nuevaCategoria, setNuevaCategoria] = useState('');
  const [nuevaCategoriaIcono, setNuevaCategoriaIcono] = useState('');
  const [nuevaSubcategoria, setNuevaSubcategoria] = useState('');
  const [nuevaSubcategoriaIcono, setNuevaSubcategoriaIcono] = useState('');
  const [nuevoServicio, setNuevoServicio] = useState('');

  // Estado para el precio por lead
  const [precioPorLead, setPrecioPorLead] = useState('');
  const [nuevoPrecioPorLead, setNuevoPrecioPorLead] = useState('');
  const [loadingPrecio, setLoadingPrecio] = useState(false);

  // Estado para manejar carga y errores
  const [loading, setLoading] = useState({
    categorias: false,
    subcategorias: false,
    servicios: false
  });
  const [error, setError] = useState({
    categorias: null,
    subcategorias: null,
    servicios: null
  });

  // Cargar categorías al montar el componente
  useEffect(() => {
    const fetchCategorias = async () => {
      try {
        setLoading(prev => ({ ...prev, categorias: true }));
        const data = await categoriaService.getCategorias();
        setCategorias(data);

        // Seleccionar la primera categoría por defecto si existe
        if (data.length > 0) {
          setCategoriaSeleccionada(data[0].id);
        }
      } catch (err) {
        setError(prev => ({ ...prev, categorias: 'Error al cargar categorías' }));
        toast.error('Error al cargar categorías');
        console.error('Error al cargar categorías:', err);
      } finally {
        setLoading(prev => ({ ...prev, categorias: false }));
      }
    };

    fetchCategorias();
  }, []);

  // Cargar subcategorías cuando cambia la categoría seleccionada
  useEffect(() => {
    if (!categoriaSeleccionada) return;

    const fetchSubcategorias = async () => {
      try {
        setLoading(prev => ({ ...prev, subcategorias: true }));
        const data = await categoriaService.getSubcategoriasByCategoria(categoriaSeleccionada);
        setSubcategorias(data);

        console.log('Subcategorías obtenidas:', data);

        // Limpiar la subcategoría seleccionada
        setSubcategoriaSeleccionada(null);
        // Limpiar los servicios y precio por lead
        setServicios([]);
        setPrecioPorLead('');
        setNuevoPrecioPorLead('');
      } catch (err) {
        setError(prev => ({ ...prev, subcategorias: 'Error al cargar subcategorías' }));
        toast.error('Error al cargar subcategorías');
        console.error('Error al cargar subcategorías:', err);
      } finally {
        setLoading(prev => ({ ...prev, subcategorias: false }));
      }
    };

    fetchSubcategorias();
  }, [categoriaSeleccionada]);

  // Cargar servicios y precio por lead cuando cambia la subcategoría seleccionada
  useEffect(() => {
    if (!subcategoriaSeleccionada) return;

    const fetchServicios = async () => {
      try {
        setLoading(prev => ({ ...prev, servicios: true }));
        const data = await categoriaService.getServiciosBySubcategoria(subcategoriaSeleccionada);
        // Agregar propiedad seleccionado a cada servicio
        const serviciosConSeleccion = data.map(servicio => ({
          ...servicio,
          seleccionado: false
        }));
        setServicios(serviciosConSeleccion);
      } catch (err) {
        setError(prev => ({ ...prev, servicios: 'Error al cargar servicios' }));
        toast.error('Error al cargar servicios');
        console.error('Error al cargar servicios:', err);
      } finally {
        setLoading(prev => ({ ...prev, servicios: false }));
      }
    };

    // Buscar la subcategoría seleccionada en el array de subcategorías para obtener el precio por lead
    const subcategoriaActual = subcategorias.find(sub => sub.id === subcategoriaSeleccionada);
    if (subcategoriaActual && subcategoriaActual.leadPriceSubcategory) {
      setPrecioPorLead(subcategoriaActual.leadPriceSubcategory);
      setNuevoPrecioPorLead(subcategoriaActual.leadPriceSubcategory);
      console.log('Precio por lead encontrado:', subcategoriaActual.leadPriceSubcategory);
    } else {
      setPrecioPorLead(0);
      setNuevoPrecioPorLead('0');
      console.log('No se encontró precio por lead para esta subcategoría');
    }

    fetchServicios();
  }, [subcategoriaSeleccionada, subcategorias]);

  // Filtrar subcategorías y servicios según la selección
  const subcategoriasFiltradas = subcategorias?.filter(sub => {
    // Comprobar tanto categoryId como categoriaId para mayor compatibilidad
    return (sub.categoryId === categoriaSeleccionada || sub.categoriaId === categoriaSeleccionada);
  }) || [];
  console.log('Subcategorías disponibles:', subcategorias);
  console.log('Subcategorías filtradas:', subcategoriasFiltradas);
  const serviciosFiltrados = servicios?.filter(serv => {
    // Comprobar tanto subcategoryId como subcategoriaId para mayor compatibilidad
    return (serv.subcategoryId === subcategoriaSeleccionada || serv.subcategoriaId === subcategoriaSeleccionada);
  }) || [];
  console.log('Servicios disponibles:', servicios);

  // Funciones para agregar elementos
  const agregarCategoria = async () => {
    if (!nuevaCategoria.trim()) return;

    try {
      setLoading(prev => ({ ...prev, categorias: true }));
      const nombreCategoria = nuevaCategoria.trim();

      // Generar una descripción automática basada en el nombre de la categoría
      const descripcion = `Categoría que agrupa todos los servicios relacionados con ${nombreCategoria.toLowerCase()}`;

      const categoriaData = {
        name: nombreCategoria,
        iconUrl: nuevaCategoriaIcono.trim() || null,
        isActive: true,
        description: descripcion
      };

      // Eliminar propiedades nulas para no enviarlas al backend
      Object.keys(categoriaData).forEach(key => {
        if (categoriaData[key] === null) {
          delete categoriaData[key];
        }
      });

      console.log('Enviando datos de categoría:', categoriaData); // Para depuración
      const categoriaCreada = await categoriaService.crearCategoria(categoriaData);

      setCategorias([...categorias, categoriaCreada]);
      setNuevaCategoria('');
      setNuevaCategoriaIcono('');
      toast.success('Categoría creada exitosamente');
    } catch (err) {
      toast.error('Error al crear categoría');
      console.error('Error al crear categoría:', err);
    } finally {
      setLoading(prev => ({ ...prev, categorias: false }));
    }
  };

  const agregarSubcategoria = async () => {
    if (!nuevaSubcategoria.trim() || !categoriaSeleccionada) return;

    try {
      setLoading(prev => ({ ...prev, subcategorias: true }));
      const nombreSubcategoria = nuevaSubcategoria.trim();

      // Buscar el nombre de la categoría seleccionada
      const categoriaActual = categorias.find(cat => cat.id === categoriaSeleccionada);
      const nombreCategoria = categoriaActual ? (categoriaActual.name || categoriaActual.nombre) : 'la categoría seleccionada';
      console.log('Categoría actual:', categoriaActual); // Para depuración

      // Generar una descripción automática basada en el nombre de la subcategoría
      const descripcion = `Subcategoría de ${nombreCategoria} que incluye servicios de ${nombreSubcategoria.toLowerCase()}`;

      const subcategoriaData = {
        name: nombreSubcategoria,
        categoryId: categoriaSeleccionada,
        iconUrl: nuevaSubcategoriaIcono.trim() || null,
        isActive: true,
        description: descripcion
      };

      // Eliminar propiedades nulas para no enviarlas al backend
      Object.keys(subcategoriaData).forEach(key => {
        if (subcategoriaData[key] === null) {
          delete subcategoriaData[key];
        }
      });

      console.log('Enviando datos de subcategoría:', subcategoriaData); // Para depuración
      const subcategoriaCreada = await categoriaService.crearSubcategoria(subcategoriaData);
      setSubcategorias([...subcategorias, subcategoriaCreada]);
      setNuevaSubcategoria('');
      setNuevaSubcategoriaIcono('');
      toast.success('Subcategoría creada exitosamente');
    } catch (err) {
      toast.error('Error al crear subcategoría');
      console.error('Error al crear subcategoría:', err);
    } finally {
      setLoading(prev => ({ ...prev, subcategorias: false }));
    }
  };

  const agregarServicio = async () => {
    if (!nuevoServicio.trim() || !subcategoriaSeleccionada) return;

    try {
      setLoading(prev => ({ ...prev, servicios: true }));
      const servicioData = {
        name: nuevoServicio.trim(),
        subcategoryId: subcategoriaSeleccionada
      };

      const servicioCreado = await categoriaService.crearServicio(servicioData);
      // Agregar propiedad seleccionado para la UI
      const servicioConSeleccion = {
        ...servicioCreado,
        seleccionado: false
      };

      setServicios([...servicios, servicioConSeleccion]);
      setNuevoServicio('');
      toast.success('Servicio creado exitosamente');
    } catch (err) {
      toast.error('Error al crear servicio');
      console.error('Error al crear servicio:', err);
    } finally {
      setLoading(prev => ({ ...prev, servicios: false }));
    }
  };

  // Funciones para eliminar elementos
  const eliminarCategoria = async (id) => {
    if (!id || id === 1) return; // No permitir eliminar la categoría principal

    try {
      setLoading(prev => ({ ...prev, categorias: true }));
      await categoriaService.eliminarCategoria(id);

      // Actualizar estado local
      setCategorias(categorias.filter(c => c.id !== id));

      // Si la categoría eliminada era la seleccionada, seleccionar otra
      if (categoriaSeleccionada === id) {
        setCategoriaSeleccionada(categorias[0]?.id || null);
      }

      toast.success('Categoría eliminada exitosamente');
    } catch (err) {
      toast.error('Error al eliminar categoría');
      console.error('Error al eliminar categoría:', err);
    } finally {
      setLoading(prev => ({ ...prev, categorias: false }));
    }
  };

  const eliminarSubcategoria = async (id) => {
    if (!id) return;

    try {
      setLoading(prev => ({ ...prev, subcategorias: true }));
      await categoriaService.eliminarSubcategoria(id);

      // Actualizar estado local
      setSubcategorias(subcategorias.filter(s => s.id !== id));

      // Si la subcategoría eliminada era la seleccionada, deseleccionar
      if (subcategoriaSeleccionada === id) {
        setSubcategoriaSeleccionada(null);
      }

      toast.success('Subcategoría eliminada exitosamente');
    } catch (err) {
      toast.error('Error al eliminar subcategoría');
      console.error('Error al eliminar subcategoría:', err);
    } finally {
      setLoading(prev => ({ ...prev, subcategorias: false }));
    }
  };

  const eliminarServicio = async (id) => {
    if (!id) return;

    try {
      setLoading(prev => ({ ...prev, servicios: true }));
      await categoriaService.eliminarServicio(id);

      // Actualizar estado local
      setServicios(servicios.filter(s => s.id !== id));

      toast.success('Servicio eliminado exitosamente');
    } catch (err) {
      toast.error('Error al eliminar servicio');
      console.error('Error al eliminar servicio:', err);
    } finally {
      setLoading(prev => ({ ...prev, servicios: false }));
    }
  };

  // Función para cambiar la selección de servicio
  const toggleServicioSeleccionado = (id) => {
    setServicios(servicios.map(s =>
      s.id === id ? { ...s, seleccionado: !s.seleccionado } : s
    ));
  };

  // Función para mapear los datos de la API a la estructura de la UI
  const mapApiDataToUi = (data, type) => {
    if (!data) return [];

    switch (type) {
      case 'categorias':
        return data.map(item => ({
          id: item.id,
          nombre: item.name || item.nombre
        }));
      case 'subcategorias':
        return data.map(item => ({
          id: item.id,
          categoriaId: item.categoryId || item.categoriaId,
          nombre: item.name || item.nombre,
          icono: item.icon || item.icono || '📄',
          leadPriceSubcategory: item.leadPriceSubcategory
        }));
      case 'servicios':
        return data.map(item => ({
          id: item.id,
          subcategoriaId: item.subcategoryId || item.subcategoriaId,
          nombre: item.name || item.nombre,
          seleccionado: false
        }));
      default:
        return data;
    }
  };

  // Función para actualizar el precio por lead
  const actualizarPrecioPorLead = async () => {
    if (!subcategoriaSeleccionada) {
      toast.warning('Seleccione una subcategoría primero');
      return;
    }

    // Validar que el precio sea un número válido
    const precio = parseFloat(nuevoPrecioPorLead);
    if (isNaN(precio) || precio < 0) {
      toast.error('El precio debe ser un número válido mayor o igual a cero');
      return;
    }

    try {
      setLoadingPrecio(true);
      await categoriaService.actualizarPrecioPorLead(subcategoriaSeleccionada, precio);

      //console.log('mostrando id y preciooooooooooooooooo', subcategoriaSeleccionada, precio);

      // Actualizar el precio en el estado local
      setPrecioPorLead(precio);

      // Actualizar el precio en la lista de subcategorías
      const subcategoriasActualizadas = subcategorias.map(sub => {
        if (sub.id === subcategoriaSeleccionada) {
          return { ...sub, leadPriceSubcategory: precio };
        }
        return sub;
      });
      setSubcategorias(subcategoriasActualizadas);

      toast.success('Precio por lead actualizado correctamente');
    } catch (error) {
      console.error('Error al actualizar precio por lead:', error);
      toast.error('Error al actualizar el precio por lead');
    } finally {
      setLoadingPrecio(false);
    }
  };

  return (
    <>
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-2">Categorías y Servicios</h2>

        {/* Mensajes de error */}
        {(error.categorias || error.subcategorias || error.servicios) && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error.categorias && <p>{error.categorias}</p>}
            {error.subcategorias && <p>{error.subcategorias}</p>}
            {error.servicios && <p>{error.servicios}</p>}
          </div>
        )}

        {/* Sección de Categorías */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-2">
          <h3 className="text-lg font-semibold mb-2">Categorías</h3>

          {/* Indicador de carga para categorías */}
          {loading.categorias && (
            <div className="flex justify-center items-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 xl:grid-cols-8 gap-4 mb-4">
            {categorias.map(categoria => (
              <div
                key={categoria.id}
                className={`
                relative rounded-lg p-4 cursor-pointer text-center h-full items-center justify-center flex flex-col
                ${categoria.id === categoriaSeleccionada
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}
              `}
                onClick={() => setCategoriaSeleccionada(categoria.id)}
              >
                <div className="flex items-center justify-center">
                  <div className="text-md font-medium">{categoria.name}</div>
                </div>
              </div>
            ))}

            {/* Formulario para agregar categoría */}
            <div className="w-[400px] rounded-lg border-2 border-dashed border-gray-300 p-4 flex flex-col items-center justify-center h-full">
              <div className="flex flex-col w-full mb-2">
                <div className="flex mb-2">
                  <button
                    className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-3 rounded-l text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => eliminarCategoria(categoriaSeleccionada)}
                    disabled={categoriaSeleccionada === 1} // No permitir eliminar la primera categoría
                    title={categoriaSeleccionada === 1 ? "No se puede eliminar la categoría principal" : "Eliminar categoría seleccionada"}
                  >
                    Eliminar
                  </button>
                  <input
                    type="text"
                    placeholder="Nueva categoría"
                    className="flex-1 p-2 border-t border-b border-l-0 border-r-0"
                    value={nuevaCategoria}
                    onChange={(e) => setNuevaCategoria(e.target.value)}
                  />
                  <button
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-3 rounded-r text-sm"
                    onClick={agregarCategoria}
                  >
                    Agregar
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="URL del icono (opcional)"
                  className="w-full p-2 border rounded"
                  value={nuevaCategoriaIcono}
                  onChange={(e) => setNuevaCategoriaIcono(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sección de Subcategorías y Servicios */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Sección de Subcategorías */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4">Subcategorías</h3>

            {/* Indicador de carga para subcategorías */}
            {loading.subcategorias && (
              <div className="flex justify-center items-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            )}

            {/* Mensaje cuando no hay categoría seleccionada */}
            {!categoriaSeleccionada && !loading.subcategorias && (
              <div className="text-center py-4 text-gray-500">
                Seleccione una categoría para ver sus subcategorías
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
              {subcategoriasFiltradas.map(subcategoria => (
                <div
                  key={subcategoria.id}
                  className={`
                  relative rounded-lg p-4 cursor-pointer
                  ${subcategoria.id === subcategoriaSeleccionada
                      ? 'bg-blue-100 border-2 border-blue-500'
                      : 'bg-gray-100 hover:bg-gray-200'}
                `}
                  onClick={() => setSubcategoriaSeleccionada(subcategoria.id)}
                >
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                      {subcategoria.iconUrl ? (
                        <img
                          src={subcategoria.iconUrl}
                          alt={subcategoria.name}
                          className="w-6 h-6 object-contain"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/24?text=💼';
                          }}
                        />
                      ) : (
                        <span className="text-blue-500 text-xl">💼</span>
                      )}
                    </div>
                    <div className="text-sm font-medium">{subcategoria.name}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Formulario para agregar subcategoría */}
            <div className="mt-4 border-t pt-4">
              <div className="flex flex-col">
                <div className="flex mb-2">
                  <button
                    className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-3 rounded-l text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => eliminarSubcategoria(subcategoriaSeleccionada)}
                    disabled={subcategoriasFiltradas.length === 0}
                    title={subcategoriasFiltradas.length === 0 ? "No hay subcategorías para eliminar" : "Eliminar subcategoría seleccionada"}
                  >
                    Eliminar
                  </button>
                  <input
                    type="text"
                    placeholder="Nueva subcategoría"
                    className="flex-1 p-2 border-t border-b border-l-0 border-r-0"
                    value={nuevaSubcategoria}
                    onChange={(e) => setNuevaSubcategoria(e.target.value)}
                  />
                  <button
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-r"
                    onClick={agregarSubcategoria}
                  >
                    Agregar
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="URL del icono (opcional)"
                  className="w-full p-2 border rounded"
                  value={nuevaSubcategoriaIcono}
                  onChange={(e) => setNuevaSubcategoriaIcono(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Sección de Servicios */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4">Servicios</h3>

            {/* Indicador de carga para servicios */}
            {loading.servicios && (
              <div className="flex justify-center items-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            )}

            {/* Mensaje cuando no hay subcategoría seleccionada */}
            {!subcategoriaSeleccionada && !loading.servicios && (
              <div className="text-center py-4 text-gray-500">
                Seleccione una subcategoría para ver sus servicios
              </div>
            )}

            <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
              {serviciosFiltrados.map(servicio => (
                <div
                  key={servicio.id}
                  className="flex items-center p-2 rounded hover:bg-gray-100"
                >
                  <input
                    type="checkbox"
                    checked={servicio.seleccionado}
                    onChange={() => toggleServicioSeleccionado(servicio.id)}
                    className="mr-3"
                  />
                  <span className="flex-1">{servicio.name}</span>
                </div>
              ))}

              {serviciosFiltrados.length === 0 && (
                <p className="text-gray-500 text-center py-4">No hay servicios para esta subcategoría</p>
              )}
            </div>

            {/* Formulario para agregar servicio */}
            <div className="mt-4 border-t pt-4">
              <div className="flex">
                <button
                  className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-3 rounded-l text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => {
                    // Encontrar el ID del servicio seleccionado (el primero que esté seleccionado)
                    const servicioSeleccionado = serviciosFiltrados.find(s => s.seleccionado);
                    if (servicioSeleccionado) {
                      eliminarServicio(servicioSeleccionado.id);
                    }
                  }}
                  disabled={!serviciosFiltrados.some(s => s.seleccionado)}
                  title={!serviciosFiltrados.some(s => s.seleccionado) ? "Seleccione un servicio para eliminar" : "Eliminar servicio seleccionado"}
                >
                  Eliminar
                </button>
                <input
                  type="text"
                  placeholder="Nuevo servicio"
                  className="flex-1 p-2 border-t border-b border-l-0 border-r-0"
                  value={nuevoServicio}
                  onChange={(e) => setNuevoServicio(e.target.value)}
                />
                <button
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-r"
                  onClick={agregarServicio}
                >
                  Agregar
                </button>
              </div>
            </div>


            {/* Componente de Precio por Lead */}
            {subcategoriaSeleccionada && (
              <div className="bg-white rounded-lg shadow-md p-2 mt-6">
                <h3 className="text-lg font-semibold mb-4">Precio por Lead</h3>


                <div className="flex items-center mb-4">
                  <span className="text-gray-700 mr-2">Precio actual:</span>
                  <span className="font-bold text-lg">
                    {precioPorLead ? new Intl.NumberFormat('es-CO', {
                      style: 'currency',
                      currency: 'COP',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    }).format(precioPorLead) : 'No definido'}
                  </span>
                </div>

                <div className="flex items-end space-x-2">
                  <div className="flex-1">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="nuevoPrecio">
                      Nuevo precio
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 text-gray-500 bg-gray-100 rounded-l-md border border-r-0 border-gray-300">
                        $
                      </span>
                      <input
                        type="number"
                        id="nuevoPrecio"
                        className="shadow appearance-none border rounded-r w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        placeholder="Ingrese el nuevo precio"
                        value={nuevoPrecioPorLead}
                        onChange={(e) => setNuevoPrecioPorLead(e.target.value)}
                      />
                    </div>
                  </div>
                  <button
                    className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center"
                    onClick={actualizarPrecioPorLead}
                    disabled={loadingPrecio}
                  >
                    {loadingPrecio ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Actualizando...
                      </>
                    ) : 'Actualizar precio'}
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </>
  );
};

export default CategoriasServicios;
